import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, GraduationCap } from 'lucide-react';

const QUICK_REPLIES = [
  "How can I apply for a scholarship?",
  "What documents do I need?",
  "Am I eligible for financial assistance?",
  "How can I donate to the trust?",
  "What support programs are available?",
  "When are scholarship applications open?",
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '🙏 Namaste! Welcome to Meenamma Educational Trust.\n\nWe are here to help students from economically disadvantaged backgrounds access quality education through scholarships and financial assistance.\n\nHow can I assist you today?',
      timestamp: new Date().toISOString(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // LlamaStack client removed - using API route

  const scrollToBottom = () => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const savedMessages = localStorage.getItem('meenamma-chat-history');
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        if (parsed.length > 1) {
          setMessages(parsed);
          setShowQuickReplies(false);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('meenamma-chat-history', JSON.stringify(messages));
  }, [messages]);

  const sendMessage = async (messageText) => {
    const textToSend = messageText || inputValue.trim();
    if (!textToSend || isLoading) return;

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowQuickReplies(false);

    try {
      // Use API endpoint for chat
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(({ role, content }) => ({
            role,
            content,
          })),
          userId: getUserId(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        timestamp: data.timestamp,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or contact us directly at the trust office.',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply);
  };

  const getUserId = () => {
    let userId = localStorage.getItem('meenamma-chat-user-id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('meenamma-chat-user-id', userId);
    }
    return userId;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[9999] font-sans">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-indigo to-brand-emerald hover:from-brand-indigo/90 hover:to-brand-emerald/90 rounded-full shadow-lg shadow-brand-indigo/30 transition-transform hover:scale-110 group"
          aria-label="Open chat"
        >
          <MessageCircle className="w-8 h-8 text-white animate-pulse group-hover:animate-none" />
          <span className="absolute -top-1 -right-1 flex h-5 w-5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-green-500"></span>
          </span>
        </button>
      )}

      {isOpen && (
        <div className="flex flex-col w-[380px] h-[600px] max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300 border border-gray-200">
          <div className="flex items-center justify-between p-5 bg-gradient-to-r from-brand-indigo to-brand-emerald text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <GraduationCap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Meenamma Trust</h3>
                <p className="text-xs opacity-90">Education Support Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close chat"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 bg-gradient-to-b from-gray-50 to-white space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-[#a3d977] to-[#8bc653] text-white rounded-br-md'
                      : 'bg-white text-gray-800 rounded-bl-md shadow-md border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start animate-in fade-in duration-300">
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-md border border-gray-100">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-brand-indigo rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-brand-emerald rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-brand-amber rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {showQuickReplies && !isLoading && messages.length === 1 && (
              <div className="space-y-2 pt-2 animate-in fade-in duration-500">
                <p className="text-xs text-gray-500 font-medium px-1">Quick questions:</p>
                <div className="grid grid-cols-1 gap-2">
                  {QUICK_REPLIES.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      className="text-left px-4 py-2.5 text-sm bg-white hover:bg-gradient-to-r hover:from-brand-indigo hover:to-brand-emerald hover:text-white border border-gray-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="flex items-center gap-2 p-4 bg-white border-t border-gray-200">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about scholarships, eligibility..."
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-indigo/40 focus:border-transparent text-sm transition-all"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading}
              className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#a3d977] to-[#8bc653] hover:from-[#8bc653] hover:to-[#7ab542] disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-all shadow-md hover:shadow-lg"
              aria-label="Send message"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-center text-gray-600">
              💚 Empowering education • Supporting dreams
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
